import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AIDisclaimer from '@/components/legal/AIDisclaimer';
import RolePicker, { DEFAULT_ROLES, INITIALLY_SELECTED } from '@/components/RolePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

// Map backend BranchProfile enum → generator industry ID (best-effort)
const PROFILE_TO_GENERATOR = {
  bygg_anlegg: 'bygg',
  eiendom: 'eiendom',
  uteliv: 'servering',
  annet: 'annet',
};

// Forhåndsdefinerte bransjer
const INDUSTRIES = [
  { id: 'bygg', name: 'Bygg og anlegg', icon: '🏗️' },
  { id: 'servering', name: 'Servering/Restaurant', icon: '🍽️' },
  { id: 'eiendom', name: 'Eiendom/Forvaltning', icon: '🏢' },
  { id: 'handel', name: 'Handel/Butikk', icon: '🛒' },
  { id: 'helse', name: 'Helse/Omsorg', icon: '🏥' },
  { id: 'kontor', name: 'Kontor/Administrasjon', icon: '💼' },
  { id: 'transport', name: 'Transport/Logistikk', icon: '🚚' },
  { id: 'annet', name: 'Annet', icon: '📋' },
];

// Miljøfyrtårn bransjekriterier (hovedkategorier)
const MF_BRANSJER = [
  { id: 'kontor', name: 'Kontor og administrasjon', desc: 'Kontorvirksomhet, rådgivning, IT' },
  { id: 'butikk_handel', name: 'Butikk og handel', desc: 'Detaljhandel, netthandel, kjøpesenter' },
  { id: 'hotell', name: 'Hotell og overnatting', desc: 'Hotell, motell, pensjonat, camping' },
  { id: 'restaurant_servering', name: 'Restaurant og servering', desc: 'Restaurant, kafé, kantine, catering' },
  { id: 'bygg_anlegg', name: 'Bygg og anlegg', desc: 'Entreprenør, håndverk, byggevirksomhet' },
  { id: 'eiendom_forvaltning', name: 'Eiendom og forvaltning', desc: 'Byggeier, eiendomsforvaltning, drift' },
  { id: 'transport_logistikk', name: 'Transport og logistikk', desc: 'Godstransport, persontransport, lager' },
  { id: 'industri', name: 'Industri og produksjon', desc: 'Produksjon, tilvirkning, foredling' },
  { id: 'helse_omsorg', name: 'Helse og omsorg', desc: 'Legekontor, tannlege, sykehjem, hjemmetjeneste' },
  { id: 'barnehage_skole', name: 'Barnehage og skole', desc: 'Barnehage, grunnskole, videregående' },
  { id: 'verksted_bil', name: 'Verksted og bilbransjen', desc: 'Bilverksted, lakkering, bilforhandler' },
  { id: 'renhold', name: 'Renholdsbedrift', desc: 'Renhold, facility management' },
  { id: 'idrett_kultur', name: 'Idrett og kultur', desc: 'Idrettsanlegg, museum, kulturhus, treningssenter' },
  { id: 'annet_mf', name: 'Annet', desc: 'Annen virksomhet' },
];

function SimpleGenerator() {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  
  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState('');
  const [mode, setMode] = useState('internkontroll');
  const [isoStandards, setIsoStandards] = useState(['iso_9001', 'iso_14001', 'iso_45001']);
  const [mfBransje, setMfBransje] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([...INITIALLY_SELECTED]);
  const [generating, setGenerating] = useState(false);

  // Pre-fill industry from company profile (if saved on org)
  useEffect(() => {
    if (!industry && organization?.industry) {
      const mapped = PROFILE_TO_GENERATOR[organization.industry];
      if (mapped) setIndustry(mapped);
    }
  }, [organization, industry]);

  const toggleIsoStandard = (stdId) => {
    setIsoStandards(prev =>
      prev.includes(stdId)
        ? prev.filter(s => s !== stdId)
        : [...prev, stdId]
    );
  };

  const modeLabels = {
    internkontroll: { title: 'IK-system', loading: 'Genererer IK-system...', done: 'IK-system generert!' },
    iso: { title: 'ISO-klargjøring', loading: 'Genererer ISO-klargjøring...', done: 'ISO-klargjøring generert!' },
    miljofyrtarn: { title: 'Miljøfyrtårn-klargjøring', loading: 'Genererer Miljøfyrtårn-klargjøring...', done: 'Miljøfyrtårn-klargjøring generert!' },
  };
  const currentLabel = modeLabels[mode] || modeLabels.internkontroll;

  const handleGenerate = async () => {
    if (!industry) {
      toast.error('Velg bransje først');
      return;
    }

    setGenerating(true);
    const toastId = toast.loading(currentLabel.loading);

    try {
      const token = localStorage.getItem('systemklart_token');
      
      // Bygg input basert på valg
      const input = {
        roller: selectedRoles.map(id => {
          const role = DEFAULT_ROLES.find(r => r.id === id);
          return role ? role.name : id.replace('custom_', '').replace(/_/g, ' ');
        }),
        risiko_faktorer: getDefaultRisks(industry),
        tekniske_anlegg: [],
        spesialkrav: [],
        mode: mode,
        sertifiseringer: mode === 'iso' ? isoStandards : mode === 'miljofyrtarn' ? ['miljofyrtarn'] : [],
        mf_bransje: mode === 'miljofyrtarn' ? mfBransje : null,
      };

      const response = await axios.post(
        `${API}/organizations/${organization.id}/generate-ik-system`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✓ ${currentLabel.done}`, { id: toastId });
      
      // Naviger til resultat
      navigate(`/ik-package/${response.data.package_id}`);
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke generere', { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  // Standard risikoer basert på bransje
  const getDefaultRisks = (ind) => {
    const risks = {
      'bygg': ['Fall fra høyde', 'Fallende gjenstander', 'Klemskader', 'Støy', 'Vibrasjoner'],
      'servering': ['Brannskader', 'Kuttskader', 'Fall/skliskader', 'Tunge løft', 'Stress'],
      'eiendom': ['Fall', 'Elektrisk fare', 'Kjemikalier', 'Ergonomi', 'Inneklima'],
      'handel': ['Tunge løft', 'Fall/skliskader', 'Ran/trusler', 'Ergonomi', 'Stress'],
      'helse': ['Smitte', 'Tunge løft', 'Vold/trusler', 'Stikkskader', 'Stress'],
      'kontor': ['Ergonomi', 'Inneklima', 'Stress', 'Fall', 'Brann'],
      'transport': ['Trafikkulykker', 'Tunge løft', 'Vibrasjoner', 'Ergonomi', 'Stress'],
      'annet': ['Fall', 'Ergonomi', 'Brann', 'Stress', 'Inneklima'],
    };
    return risks[ind] || risks['annet'];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold">S</div>
              <span className="text-gray-500">← Tilbake</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Generer {currentLabel.title}
        </h1>
        <p className="text-gray-600 mb-8">
          Svar på noen enkle spørsmål, så genererer vi et komplett system.
        </p>

        {/* Steg-indikator */}
        <div className="flex items-center gap-2 mb-8">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
          }`}>1</div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
          }`}>2</div>
        </div>

        {/* Steg 1: Bransje og modus */}
        {step === 1 && (
          <div className="space-y-8">
            {/* Bransje */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Hvilken bransje er dere i?
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind.id)}
                    className={`p-4 rounded-lg border-2 text-center transition-all ${
                      industry === ind.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{ind.icon}</div>
                    <div className="text-sm font-medium">{ind.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modus */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Hva er målet ditt?
              </h2>
              <div className="space-y-2">
                {[
                  { id: 'internkontroll', name: 'Kun internkontroll', desc: 'Oppfylle lovkrav' },
                  { id: 'iso', name: 'Klargjøring for ISO', desc: 'ISO 9001, 14001 og 45001' },
                  { id: 'miljofyrtarn', name: 'Klargjøring for Miljøfyrtårn', desc: 'Miljøsertifisering' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      mode === m.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium">{m.name}</div>
                    <div className="text-sm text-gray-500">{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* ISO-standarder (vises kun når ISO er valgt) */}
            {mode === 'iso' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Hvilke ISO-standarder gjelder?
                </h2>
                <p className="text-gray-600 mb-4">
                  Velg én eller flere standarder du ønsker å klargjøre for.
                </p>
                <div className="space-y-2">
                  {[
                    { id: 'iso_9001', name: 'ISO 9001', desc: 'Kvalitetsledelse' },
                    { id: 'iso_14001', name: 'ISO 14001', desc: 'Miljøledelse' },
                    { id: 'iso_45001', name: 'ISO 45001', desc: 'HMS-ledelse' },
                  ].map(std => (
                    <button
                      key={std.id}
                      onClick={() => toggleIsoStandard(std.id)}
                      className={`w-full p-4 rounded-lg border-2 text-left flex items-center justify-between transition-all ${
                        isoStandards.includes(std.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{std.name}</div>
                        <div className="text-sm text-gray-500">{std.desc}</div>
                      </div>
                      {isoStandards.includes(std.id) && (
                        <span className="text-blue-600 text-lg">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Miljøfyrtårn bransjekriterier (vises kun når MF er valgt) */}
            {mode === 'miljofyrtarn' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Hvilken bransje gjelder sertifiseringen?
                </h2>
                <p className="text-gray-600 mb-4">
                  Miljøfyrtårn har egne bransjekriterier i tillegg til felleskriteriene. Velg den som passer best.
                </p>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {MF_BRANSJER.map(b => (
                    <button
                      key={b.id}
                      onClick={() => setMfBransje(b.id)}
                      className={`w-full p-4 rounded-lg border-2 text-left flex items-center justify-between transition-all ${
                        mfBransje === b.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{b.name}</div>
                        <div className="text-sm text-gray-500">{b.desc}</div>
                      </div>
                      {mfBransje === b.id && (
                        <span className="text-green-600 text-lg">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setStep(2)}
              disabled={!industry || (mode === 'iso' && isoStandards.length === 0) || (mode === 'miljofyrtarn' && !mfBransje)}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        )}

        {/* Steg 2: Roller */}
        {step === 2 && (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Hvilke roller har dere?
              </h2>
              <p className="text-gray-600 mb-4">
                Velg aktuelle roller, eller legg til egne.
              </p>
              
              <RolePicker
                selectedRoles={selectedRoles}
                onRolesChange={setSelectedRoles}
                variant="light"
              />
            </div>

            {/* AI Disclaimer */}
            <AIDisclaimer variant="before" />

            {/* Sammendrag */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Oppsummering</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Bransje: {INDUSTRIES.find(i => i.id === industry)?.name}</li>
                {organization?.nace_code && (
                  <li data-testid="summary-nace-line">
                    NACE: <span className="font-mono">{organization.nace_code}</span>
                    {organization?.nace_description && <> — {organization.nace_description}</>}
                  </li>
                )}
                <li>Mål: {mode === 'internkontroll' ? 'Kun internkontroll (IK §5)' : mode === 'iso' ? 'ISO-klargjøring' : 'Miljøfyrtårn-klargjøring'}</li>
                {mode === 'iso' && (
                  <li>Standarder: {isoStandards.map(s => s.replace('iso_', 'ISO ').replace('_', '')).join(', ')}</li>
                )}
                {mode === 'miljofyrtarn' && mfBransje && (
                  <li>MF-bransjekriterier: {MF_BRANSJER.find(b => b.id === mfBransje)?.name}</li>
                )}
                <li>Roller: {selectedRoles.length} valgt</li>
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 font-medium mb-1">Dokumenter som genereres:</p>
                <ul className="text-xs text-gray-500 space-y-0.5">
                  <li>• Lov- og forskriftsmatrise</li>
                  <li>• Prosedyrer og rutiner</li>
                  <li>• Roller og ansvar</li>
                  <li>• Årshjul</li>
                  {mode === 'iso' && isoStandards.includes('iso_9001') && (
                    <li>• ISO 9001 – Kvalitetsledelse</li>
                  )}
                  {mode === 'iso' && isoStandards.includes('iso_14001') && (
                    <li>• ISO 14001 – Miljøledelse</li>
                  )}
                  {mode === 'iso' && isoStandards.includes('iso_45001') && (
                    <li>• ISO 45001 – HMS-ledelse</li>
                  )}
                  {mode === 'miljofyrtarn' && (
                    <>
                      <li>• Miljøfyrtårn felleskriterier</li>
                      <li>• Bransjekriterier: {MF_BRANSJER.find(b => b.id === mfBransje)?.name}</li>
                      <li>• Klimaregnskap og miljørapportering</li>
                      <li>• Energi, avfall, innkjøp og transport</li>
                    </>
                  )}
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
              >
                ← Tilbake
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || selectedRoles.length === 0}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {generating ? 'Genererer...' : `Generer ${currentLabel.title}`}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SimpleGenerator;
